// Shared rules for the /[lang]/[city]/events/[slice] pages: one city's events
// in a date window (`today`, `this-weekend`, `this-week`) or in one category
// (`concerts`, `theatre`…). Framework-free so the build-time prefetch
// (list-prefetch.ts), generateStaticParams and the screen all agree — same
// role as city-listings.ts for /[city]/events.
//
// They answer the queries the date and category filters can't, since filter
// state isn't in a URL: "concert nice", "what's on in nice this weekend",
// "que faire à nice ce week-end", "куда сходить в ницце сегодня" — the last
// kind being exactly what people ask AI assistants, which need a page to cite.

import { categories } from '@/lib/events-data';
import type { DbEvent } from '@todo/core/supabase';

/** The date-window slices, in display order. Every city with a /[city]/events page gets all three. */
export const EVENT_WINDOW_SLICES = ['today', 'this-weekend', 'this-week'] as const;
export type EventWindowSlice = (typeof EVENT_WINDOW_SLICES)[number];

/** Each window slice's getDateRange() id (Europe/Paris days, like the /events "when" filter). */
export const WINDOW_DATE_ID: Record<EventWindowSlice, 'today' | 'weekend' | 'week'> = {
  today: 'today',
  'this-weekend': 'weekend',
  'this-week': 'week',
};

/**
 * A city × category page is exported only with at least this many upcoming
 * shows — below it, it's a thin copy of the city's /[city]/events page. Same
 * role as MIN_BOATS_PER_TYPE_PAGE.
 */
export const MIN_EVENTS_PER_CATEGORY_PAGE = 10;

/** How many soonest shows a slice page shows as full cards; the rest are text links. */
export const TOP_EVENTS_ON_SLICE_PAGE = 12;

/** How many upcoming shows an empty window page offers instead ("coming up next"). */
export const NEXT_EVENTS_ON_EMPTY_WINDOW = 6;

/** City id → the category ids with an exported page, in `categories` order. */
export type CityEventCategoryPages = Record<string, string[]>;

export function isWindowSlice(slice: string): slice is EventWindowSlice {
  return (EVENT_WINDOW_SLICES as readonly string[]).includes(slice);
}

/**
 * The category pages to export for one city, from its full /[city]/events
 * list (one row per show). `other` never gets one: it's a leftover, not a
 * thing anyone searches for.
 */
export function cityEventCategoryIds(events: Pick<DbEvent, 'category_id'>[]): string[] {
  const counts = new Map<string, number>();
  for (const event of events) {
    const id = event.category_id ?? 'other';
    counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  return categories
    .map((category) => category.id)
    .filter((id) => id !== 'other' && (counts.get(id) ?? 0) >= MIN_EVENTS_PER_CATEGORY_PAGE);
}

/**
 * What a slice page bakes. `from` / `until` (ISO, until exclusive) are the
 * window the export computed — a window page renders those, not "now", so the
 * browser's first render matches the HTML. `builtAt` is the export time (the
 * page's visible "updated" date and `dateModified`). `next` is only set on an
 * empty window: the city's soonest shows, offered instead.
 */
export type EventSliceSeed = {
  rows: DbEvent[];
  from: string | null;
  until: string | null;
  builtAt: string;
  next?: DbEvent[];
};
